import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, Loader2, Save } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { adminApi, ApiError, type AdminWorkspaceUpdate } from '@/lib/api';
import { PLATFORMS } from '@/lib/platforms';
import { PlatformIcon } from '@/components/ui/PlatformIcon';
import { AdminModal, SearchBox, formatDate, EmptyState, TableSkeleton } from './AdminShared';

export function AdminWorkspaces() {
  const { getToken } = useAuth();
  const [search, setSearch] = useState('');
  const [agencyFilter, setAgencyFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: agenciesData } = useQuery({
    queryKey: ['admin-agencies-all'],
    queryFn: async () => {
      const token = await getToken();
      return adminApi.listAgencies(token!, { limit: 200 });
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['admin-workspaces', search, agencyFilter],
    queryFn: async () => {
      const token = await getToken();
      return adminApi.listWorkspaces(token!, {
        search: search || undefined,
        agencyId: agencyFilter || undefined,
      });
    },
  });

  const agencies = agenciesData?.agencies ?? [];
  const workspaces = data?.workspaces ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <SearchBox value={search} onChange={setSearch} placeholder="Search workspaces…" />
          <select
            value={agencyFilter}
            onChange={(e) => setAgencyFilter(e.target.value)}
            className="bg-input border border-border rounded-md px-2 py-1.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-accent"
          >
            <option value="">All agencies</option>
            {agencies.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <span className="text-xs text-muted-foreground">{data?.total ?? workspaces.length} total</span>
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : workspaces.length === 0 ? (
        <EmptyState message="No workspaces found" />
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-muted-foreground text-left">
                <th className="px-4 py-2.5 font-medium">Workspace</th>
                <th className="px-4 py-2.5 font-medium">Agency</th>
                <th className="px-4 py-2.5 font-medium text-center">Accounts</th>
                <th className="px-4 py-2.5 font-medium text-center">Posts</th>
                <th className="px-4 py-2.5 font-medium text-center">Trends</th>
                <th className="px-4 py-2.5 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {workspaces.map((w) => (
                <tr
                  key={w.id}
                  onClick={() => setSelectedId(w.id)}
                  className="border-b border-border/60 last:border-0 hover:bg-muted/30 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-2.5 font-medium text-foreground">{w.name}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{w.agency_name ?? '—'}</td>
                  <td className="px-4 py-2.5 text-center text-muted-foreground">{w.social_account_count}</td>
                  <td className="px-4 py-2.5 text-center text-muted-foreground">{w.post_count}</td>
                  <td className="px-4 py-2.5 text-center">
                    {w.trends_enabled === 1 ? (
                      <span className="text-[10px] text-success">On</span>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">Off</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">{formatDate(w.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedId && (
        <WorkspaceDetail
          id={selectedId}
          agencies={agencies.map((a) => ({ id: a.id, name: a.name }))}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

function parseJsonArray(value: unknown): string {
  if (typeof value !== 'string' || !value) return '';
  try {
    const arr = JSON.parse(value);
    return Array.isArray(arr) ? arr.join(', ') : '';
  } catch {
    return '';
  }
}

function toArray(value: string): string[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function WorkspaceDetail({
  id,
  agencies,
  onClose,
}: {
  id: string;
  agencies: Array<{ id: string; name: string }>;
  onClose: () => void;
}) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [form, setForm] = useState<AdminWorkspaceUpdate & { brand_colors_text: string; services_text: string }>({
    name: '',
    website: '',
    brand_voice: '',
    tone: '',
    target_audience: '',
    ai_caption_guidance: '',
    ai_hashtag_strategy: '',
    agency_id: '',
    brand_colors_text: '',
    services_text: '',
  });
  const [trendsEnabled, setTrendsEnabled] = useState(false);
  const [injectIntoAi, setInjectIntoAi] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-workspace', id],
    queryFn: async () => {
      const token = await getToken();
      return adminApi.getWorkspace(token!, id);
    },
  });

  // Seed the form once detail loads.
  useEffect(() => {
    if (!data) return;
    const w = data.workspace;
    setForm({
      name: w.name ?? '',
      website: w.website ?? '',
      brand_voice: w.brand_voice ?? '',
      tone: w.tone ?? '',
      target_audience: w.target_audience ?? '',
      ai_caption_guidance: w.ai_caption_guidance ?? '',
      ai_hashtag_strategy: w.ai_hashtag_strategy ?? '',
      agency_id: w.agency_id ?? '',
      brand_colors_text: parseJsonArray(w.brand_colors),
      services_text: parseJsonArray(w.services),
    });
    setTrendsEnabled(data.trendConfig?.enabled === 1);
    setInjectIntoAi((data.trendConfig?.inject_into_ai as number) === 1);
  }, [data]);

  const save = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      const payload: AdminWorkspaceUpdate = {
        name: form.name,
        website: form.website || null,
        brand_voice: form.brand_voice || null,
        tone: form.tone || null,
        target_audience: form.target_audience || null,
        ai_caption_guidance: form.ai_caption_guidance || null,
        ai_hashtag_strategy: form.ai_hashtag_strategy || null,
        brand_colors: toArray(form.brand_colors_text),
        services: toArray(form.services_text),
      };
      if (form.agency_id && form.agency_id !== data?.workspace.agency_id) {
        payload.agency_id = form.agency_id;
      }
      await adminApi.updateWorkspace(token!, id, payload);
      await adminApi.updateWorkspaceTrendConfig(token!, id, {
        enabled: trendsEnabled,
        inject_into_ai: injectIntoAi,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-workspaces'] });
      qc.invalidateQueries({ queryKey: ['admin-workspace', id] });
      setError(null);
      onClose();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed to save'),
  });

  const remove = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return adminApi.deleteWorkspace(token!, id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-workspaces'] });
      qc.invalidateQueries({ queryKey: ['admin-stats'] });
      onClose();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed to delete'),
  });

  const update = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  return (
    <AdminModal title={isLoading ? 'Workspace' : `Edit · ${data?.workspace.name ?? ''}`} onClose={onClose} wide>
      {isLoading || !data ? (
        <TableSkeleton rows={6} />
      ) : (
        <div className="space-y-4">
          <Field label="Name">
            <input className={inputCls} value={form.name ?? ''} onChange={(e) => update({ name: e.target.value })} />
          </Field>

          <Field label="Agency">
            <select
              className={inputCls}
              value={form.agency_id ?? ''}
              onChange={(e) => update({ agency_id: e.target.value })}
            >
              {agencies.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            {form.agency_id !== data.workspace.agency_id && (
              <p className="text-[10px] text-warning mt-1">Will reassign this workspace to a different agency.</p>
            )}
          </Field>

          <Field label="Website">
            <input className={inputCls} value={form.website ?? ''} onChange={(e) => update({ website: e.target.value })} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Brand voice">
              <input className={inputCls} value={form.brand_voice ?? ''} onChange={(e) => update({ brand_voice: e.target.value })} />
            </Field>
            <Field label="Tone">
              <input className={inputCls} value={form.tone ?? ''} onChange={(e) => update({ tone: e.target.value })} />
            </Field>
          </div>

          <Field label="Target audience">
            <input
              className={inputCls}
              value={form.target_audience ?? ''}
              onChange={(e) => update({ target_audience: e.target.value })}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Brand colors (comma-separated)">
              <input
                className={inputCls}
                value={form.brand_colors_text}
                onChange={(e) => update({ brand_colors_text: e.target.value })}
                placeholder="#0C1830, #6B8FFE"
              />
            </Field>
            <Field label="Services (comma-separated)">
              <input
                className={inputCls}
                value={form.services_text}
                onChange={(e) => update({ services_text: e.target.value })}
              />
            </Field>
          </div>

          <Field label="AI caption guidance">
            <textarea
              className={`${inputCls} h-16 resize-none`}
              value={form.ai_caption_guidance ?? ''}
              onChange={(e) => update({ ai_caption_guidance: e.target.value })}
            />
          </Field>

          <Field label="AI hashtag strategy">
            <input
              className={inputCls}
              value={form.ai_hashtag_strategy ?? ''}
              onChange={(e) => update({ ai_hashtag_strategy: e.target.value })}
            />
          </Field>

          {/* Trend config */}
          <div className="border border-border rounded-lg p-3 space-y-2">
            <h4 className="text-xs font-semibold text-foreground">Trend harvesting</h4>
            <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
              <input type="checkbox" checked={trendsEnabled} onChange={(e) => setTrendsEnabled(e.target.checked)} />
              Enabled
            </label>
            <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
              <input type="checkbox" checked={injectIntoAi} onChange={(e) => setInjectIntoAi(e.target.checked)} />
              Inject trends into AI prompts
            </label>
          </div>

          {/* Read-only social accounts */}
          {data.socialAccounts.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-foreground mb-2">Connected accounts</h4>
              <div className="flex flex-wrap gap-1.5">
                {data.socialAccounts.map((acc) => (
                  <span key={acc.id} className="inline-flex items-center gap-1 text-[10px] bg-muted/60 rounded-full px-2 py-0.5 text-muted-foreground">
                    <PlatformIcon platform={acc.platform} variant="glyph" size="xs" />
                    {PLATFORMS[acc.platform as keyof typeof PLATFORMS]?.label ?? acc.platform}
                    {acc.account_name ? ` · ${acc.account_name}` : ''}
                  </span>
                ))}
              </div>
            </div>
          )}

          {error && <p className="text-xs text-danger">{error}</p>}

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-border">
            {confirmDelete ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-danger">Delete permanently?</span>
                <button
                  onClick={() => remove.mutate()}
                  disabled={remove.isPending}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-danger text-danger-foreground rounded-md hover:bg-danger/90 disabled:opacity-50"
                >
                  {remove.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Yes, delete
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="px-2.5 py-1.5 text-xs text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-danger hover:bg-danger/10 rounded-md transition-colors"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete workspace
              </button>
            )}

            <button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs bg-accent text-accent-foreground rounded-md hover:bg-accent/90 disabled:opacity-50"
            >
              {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save changes
            </button>
          </div>
        </div>
      )}
    </AdminModal>
  );
}

const inputCls =
  'w-full bg-input border border-border rounded-md px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-accent';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-muted-foreground mb-1">{label}</label>
      {children}
    </div>
  );
}
